import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuthenticatedApiClient } from "./useAuthenticatedApiClient";
import UnderwritingRuleResultsApiClient from "@/api/underwritingRuleResultsApiClient";
import { UnderwritingRuleResultUpdateDTO } from "@/types/UnderwritingRuleResultUpdateDTO";
import { useFieldSchemaV2FactoryContext, FieldFormV2Module, SchemaFactoryV2, FieldSchemaFormBuilderV2 } from "@ucl/library";
import { createUnderwritingRuleResultFormBuilder } from "@/builders/underwritingRuleResultFormBuilder";
import { useAutoSave } from "./useAutoSave";

export interface UseUnderwritingRuleResultFormResult {
  formValue: UnderwritingRuleResultUpdateDTO | null;
  setFormValue: (newValue: UnderwritingRuleResultUpdateDTO, newFieldValue?: any) => void;
  saveFormData: (value: UnderwritingRuleResultUpdateDTO) => Promise<void>;
  schemaFactory?: SchemaFactoryV2;
  formBuilder?: FieldSchemaFormBuilderV2<UnderwritingRuleResultUpdateDTO>;
  fieldFormModule: React.RefObject<FieldFormV2Module<UnderwritingRuleResultUpdateDTO> | null>;
  loading: boolean;
  error: string | null;
}

export const useUnderwritingRuleResultForm = (
  ruleResultId: string
): UseUnderwritingRuleResultFormResult => {
  const [formValue, setFormValue] = useState<UnderwritingRuleResultUpdateDTO | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const underwritingRuleResultsApiClient = useAuthenticatedApiClient(UnderwritingRuleResultsApiClient);

  const fieldFormModule = useRef<FieldFormV2Module<UnderwritingRuleResultUpdateDTO> | null>(null);

  const { schemaFactory } = useFieldSchemaV2FactoryContext();

  const formBuilder = useMemo(() => {
    if (!schemaFactory) return undefined;
    return createUnderwritingRuleResultFormBuilder(schemaFactory);
  }, [schemaFactory]);

  // Use the generic auto-save hook
  const { markDirty } = useAutoSave({
    saveFunction: async () => {
      if (formValue) {
        await fieldFormModule.current?.submit();
      }
    },
    debounceDelay: 1000,
  });

  // Fetch the current underwriting rule result
  useEffect(() => {
    const fetchUnderwritingRuleResult = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await underwritingRuleResultsApiClient.getRuleResultById(ruleResultId);
        
        const updateDTO: UnderwritingRuleResultUpdateDTO = {
          id: response.id,
          resultType: response.resultType,
          resultComment: response.resultComment,
        };
        
        setFormValue(updateDTO);
      } catch (err) {
        console.error("Failed to fetch underwriting rule result:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch underwriting rule result");
      } finally {
        setLoading(false);
      }
    };

    fetchUnderwritingRuleResult();
  }, [ruleResultId, underwritingRuleResultsApiClient]);

  const setFormValueWithAutoSave = useCallback(
    async (newValue: UnderwritingRuleResultUpdateDTO, _newFieldValue?: any) => {
      setFormValue(newValue);
      // Mark as dirty and trigger auto-save
      markDirty();
    },
    [markDirty]
  );

  const saveFormData = useCallback(
    async (value: UnderwritingRuleResultUpdateDTO) => {
      try {
        setError(null);

        const response = await underwritingRuleResultsApiClient.updateRuleResult(
          ruleResultId,
          value
        );
        
        const updateDTO: UnderwritingRuleResultUpdateDTO = {
          id: response.id,
          resultType: response.resultType,
          resultComment: response.resultComment,
        };
        
        setFormValue(updateDTO);
      } catch (err) {
        console.error("Failed to update underwriting rule result:", err);
        setError(err instanceof Error ? err.message : "Failed to update underwriting rule result");
        throw err;
      }
    },
    [ruleResultId, underwritingRuleResultsApiClient]
  );

  return {
    formValue,
    setFormValue: setFormValueWithAutoSave,
    saveFormData,
    schemaFactory,
    formBuilder,
    fieldFormModule,
    loading,
    error,
  };
};
